/**
 * Sentry Error Tracking Configuration
 *
 * This module initializes Sentry for error tracking and performance monitoring.
 *
 * Setup Instructions:
 * 1. Create a Sentry account at https://sentry.io
 * 2. Create a new React project in Sentry
 * 3. Get your DSN from Project Settings
 * 4. Add to environment variables:
 *    - REACT_APP_SENTRY_DSN (both Production and Preview in Vercel)
 *    - REACT_APP_SENTRY_ENVIRONMENT (production/staging/development)
 * 5. Install Sentry SDK: npm install --save @sentry/react
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

/**
 * Initialize Sentry error tracking
 * Only initializes in production and staging environments
 */
export function initSentry() {
  // Don't initialize Sentry in development or if DSN is missing
  if (!SENTRY_DSN || ENVIRONMENT === 'development') {
    // Silently skip Sentry in development - this is expected behavior
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    console.log('Sentry not initialized (development mode or missing DSN)');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Filter out certain errors
    beforeSend(event, hint) {
      // Don't send errors for network issues in development
      if (ENVIRONMENT !== 'production') {
        const error = hint.originalException;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message);
          // Filter out common dev errors
          if (
            message.includes('Network request failed') ||
            message.includes('Failed to fetch') ||
            message.includes('Load failed')
          ) {
            return null;
          }
        }
      }

      return event;
    },

    // Release tracking
    release: process.env.REACT_APP_VERSION || '1.0.0',

    // Additional configuration
    normalizeDepth: 10, // Normalize objects to 10 levels deep
    maxBreadcrumbs: 50, // Keep last 50 breadcrumbs

    // Enable debug mode in staging
    debug: ENVIRONMENT === 'staging',
  });

  console.log(`✅ Sentry initialized for ${ENVIRONMENT} environment`);
}

/**
 * Manually capture an exception
 * Useful for try-catch blocks where you want to log to Sentry
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (ENVIRONMENT === 'development') {
    console.error('Sentry (dev mode):', error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Manually capture a message
 * Useful for logging important events
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (ENVIRONMENT === 'development') {
    console.log(`Sentry (dev mode) [${level}]:`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Set user context for Sentry
 * Call this after user login
 */
export function setUserContext(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

/**
 * Clear user context
 * Call this on logout
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(category: string, message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  });
}

// Export Sentry for direct use if needed
export { Sentry };
